/**
 * @file vision-ap-mouse.ts
 * @description 维盛 AP 鼠标 HID 报文编解码。通道层会单独处理 Report ID。
 */

import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'

/** 维盛 AP 鼠标控制报文的已验证传输参数。 */
export const VISION_AP_MOUSE_HID = {
  controlReportId: 0x04,
  /** 第 4 节 consumer 主动上报 Report ID。 */
  consumerReportId: 0x03,
  packetByteLength: 64,
  payloadByteLength: 63,
  maximumDataLength: 56,
} as const

/** V0104/V0105：功能变量区与 profile 的已确认布局。 */
export const VISION_AP_MOUSE_FUNCTION_AREA = {
  byteLength: 128,
  /** V0105 固件确认：profile 0..7，共 8 个固定槽位。 */
  profileCount: 8,
  /** 当前 profile 索引只保存在 profile 0 的 `func_ram_tab[0]`。 */
  currentProfileIndexOffset: 0,
  /** `func_ram_tab[97]`：接收器切换能力标志，默认 0，启用时写 1。 */
  receiverSwitchOffset: 97,
  /** `func_ram_tab[98]`：低电量功能指示，默认 1（打开），0 为关闭。 */
  lowBatteryIndicatorOffset: 98,
} as const

/** 将 profile 内局部地址映射到 A4/A5/A7/A8 的全局 16-bit ADDR。 */
export function getVisionApProfileBaseAddress(profileIndex: number): number {
  assertVisionApProfileIndex(profileIndex)
  return profileIndex * VISION_AP_MOUSE_FUNCTION_AREA.byteLength
}

/** V0106 两个名称区共用的槽位宽度：每个名称固定 16 字节。 */
const VISION_AP_NAME_SLOT_BYTE_LENGTH = 16

/**
 * V0106 §2.2.16/§2.2.17：profile 名称区布局。
 * 8 个 profile 各占 16 字节，`Profile_1` 对应 SDK 索引 0；读写都必须覆盖完整 128 字节。
 */
export const VISION_AP_MOUSE_PROFILE_NAME_AREA = {
  slotByteLength: VISION_AP_NAME_SLOT_BYTE_LENGTH,
  slotCount: VISION_AP_MOUSE_FUNCTION_AREA.profileCount,
  byteLength: VISION_AP_NAME_SLOT_BYTE_LENGTH * VISION_AP_MOUSE_FUNCTION_AREA.profileCount,
} as const

/**
 * V0106 §2.2.14/§2.2.15：宏名称区布局。
 * 15 个宏各占 16 字节，`Macro_1` 对应 SDK 宏索引 0；读写都必须覆盖完整 240 字节。
 * TODO(V0106): slotCount=15 已由协议确认（「新建 15 宏」）；`mouse-keys.assertMacroIndex` 尚未按此上限拒绝写入。
 */
export const VISION_AP_MOUSE_MACRO_NAME_AREA = {
  slotByteLength: VISION_AP_NAME_SLOT_BYTE_LENGTH,
  slotCount: 15,
  byteLength: VISION_AP_NAME_SLOT_BYTE_LENGTH * 15,
} as const

/** profile 名称槽位在名称区内的起始偏移：`profileIndex * 16`。 */
export function getVisionApProfileNameSlotOffset(profileIndex: number): number {
  assertVisionApProfileIndex(profileIndex)
  return profileIndex * VISION_AP_MOUSE_PROFILE_NAME_AREA.slotByteLength
}

/** 宏索引是否落在 V0106 宏名称区的 15 个槽位内；超出的宏没有设备侧名称。 */
export function hasVisionApMacroNameSlot(macroIndex: number): boolean {
  return (
    Number.isInteger(macroIndex) &&
    macroIndex >= 0 &&
    macroIndex < VISION_AP_MOUSE_MACRO_NAME_AREA.slotCount
  )
}

/** 宏名称槽位在名称区内的起始偏移：`macroIndex * 16`。 */
export function getVisionApMacroNameSlotOffset(macroIndex: number): number {
  if (!hasVisionApMacroNameSlot(macroIndex)) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `维盛 AP 宏名称槽位索引必须为 0-${VISION_AP_MOUSE_MACRO_NAME_AREA.slotCount - 1}`,
    )
  }
  return macroIndex * VISION_AP_MOUSE_MACRO_NAME_AREA.slotByteLength
}

/**
 * 将配置名称编码为 16 字节名称槽位。
 *
 * 协议只定义地址范围，固件不解析名称内容；编码沿用 Rapoo 既有配置名规范
 * （与 Nordic/WCH/Telink 键盘一致）：UTF-8、最长 16 字节、不足补 `0x00`。
 * 空名称视为删除语义，本协议不支持删除 profile，因此直接拒绝。
 */
export function encodeVisionApProfileName(name: string): Uint8Array {
  const trimmed = name.trim()
  if (trimmed.length === 0) {
    throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
  }
  const bytes = new TextEncoder().encode(trimmed)
  if (bytes.byteLength > VISION_AP_NAME_SLOT_BYTE_LENGTH) {
    throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
  }
  const slot = new Uint8Array(VISION_AP_NAME_SLOT_BYTE_LENGTH)
  slot.set(bytes)
  return slot
}

/**
 * 将宏名称编码为 16 字节名称槽位。
 *
 * 与 profile 名称同一套 UTF-8 / `0x00` 补齐约定，但沿用 Rapoo 其它鼠标宏名称的处理方式：
 * 超过 16 字节时在字符边界截断而不是拒绝（Web 宏名输入不校验字节数，写入失败也不会提示用户）；
 * 空名称编码为全 `0x00`，表示该槽位未命名。
 */
export function encodeVisionApMacroName(name: string): Uint8Array {
  const slot = new Uint8Array(VISION_AP_NAME_SLOT_BYTE_LENGTH)
  const encoder = new TextEncoder()
  let text = ''
  for (const character of name.trim()) {
    const candidate = text + character
    if (encoder.encode(candidate).byteLength > VISION_AP_NAME_SLOT_BYTE_LENGTH) break
    text = candidate
  }
  slot.set(encoder.encode(text.trimEnd()))
  return slot
}

/** 解码 profile 名称槽位；`null` 表示未命名。 */
export function decodeVisionApProfileName(slot: Uint8Array): string | null {
  return decodeVisionApNameSlot(slot, 'profile 名称槽位')
}

/** 解码宏名称槽位；`null` 表示未命名。 */
export function decodeVisionApMacroName(slot: Uint8Array): string | null {
  return decodeVisionApNameSlot(slot, '宏名称槽位')
}

/**
 * 解码 16 字节名称槽位；返回 `null` 表示未命名，由调用方回退默认展示名。
 * 首字节 `0x00`/`0xFF`（未写入或擦除态）以及非法 UTF-8 都视为未命名，不把垃圾字节展示给用户。
 */
function decodeVisionApNameSlot(slot: Uint8Array, field: string): string | null {
  if (slot.byteLength !== VISION_AP_NAME_SLOT_BYTE_LENGTH) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `${field}必须为 ${VISION_AP_NAME_SLOT_BYTE_LENGTH} 字节`,
    )
  }
  const first = slot[0]
  if (first === 0x00 || first === 0xff) return null
  const terminator = slot.indexOf(0x00)
  const bytes = terminator === -1 ? slot : slot.subarray(0, terminator)
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return text.trim().length > 0 ? text : null
  } catch {
    return null
  }
}

/** V0105 profile 对外统一使用 0..7。 */
export function assertVisionApProfileIndex(profileIndex: number): void {
  if (
    !Number.isInteger(profileIndex) ||
    profileIndex < 0 ||
    profileIndex >= VISION_AP_MOUSE_FUNCTION_AREA.profileCount
  ) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `维盛 AP profile 索引必须为 0-${VISION_AP_MOUSE_FUNCTION_AREA.profileCount - 1}`,
    )
  }
}

/** 维盛 AP 鼠标命令字。 */
export const VISION_AP_MOUSE_COMMAND = {
  // 厂商确认（2026-08-18）：VT2 ULTRA V2 链路不需要发送 A1/A2，SDK 不发送这两条命令；
  // 此处仅保留协议定义供查阅。
  beginReceiverPassthrough: 0xa1,
  endReceiverPassthrough: 0xa2,
  getFunctionArea: 0xa4,
  setFunctionArea: 0xa5,
  // 厂商确认 A7 的请求参数与 A6 相同；真实回包样本待后续补录。
  getCurrentKeyArea: 0xa7,
  setCurrentKeyArea: 0xa8,
  restoreFactorySettings: 0xa9,
  getMacroArea: 0xaa,
  setMacroArea: 0xab,
  getBatteryLevel: 0xad,
  getConnectionStatus: 0xae,
  /** 协议命令；仅产测 Qt 使用。aHub/SDK 不暴露改 PID。 */
  changeMousePidVid: 0xaf,
  /** V0104：接收器进配对；will not reply，结果看 consumer list。 */
  enterDonglePairing: 0x92,
  /** 协议命令；仅产测 Qt 使用。aHub/SDK 不暴露改 PID。 */
  changeDonglePidVid: 0x91,
  readDongleVersion: 0x93,
  readMouseVersion: 0xb0,
  /**
   * V0104：鼠标进配对；will not reply。
   * PDF 英文误抄 dongle 句；验证包/Qt：`04 00 00 B1`（Cs 固定 0）。
   */
  enterMousePairing: 0xb1,
  /** V0106 §2.2.14：读宏名称区（15 × 16 字节）。 */
  readMacroName: 0xb9,
  /** V0106 §2.2.15：写宏名称区；即使只改一个名称也必须整区写满 240 字节。 */
  writeMacroName: 0xba,
  /** V0106 §2.2.16：读 profile 名称区（8 × 16 字节）。 */
  readProfileName: 0xbb,
  /** V0106 §2.2.17：写 profile 名称区；即使只改一个名称也必须整区写满 128 字节。 */
  writeProfileName: 0xbc,
  /** V0106 §2.2.18：读电池最低/最高续航时间（小时，大端）。 */
  readBatteryRuntime: 0xbd,
} as const

/** V0104 §4 / 验证包：dongle 配对 consumer 上报（通道已剥离 Report ID 后的 Code_L/Code_H）。 */
export const VISION_AP_DONGLE_PAIRING_REPORT = {
  codeLow: 0xff,
  enter: 0xfd,
  success: 0xff,
  overtime: 0xfe,
} as const

/** V0105 Function synchronization：通知主机重新读取功能变量区。 */
export const VISION_AP_FUNCTION_SYNCHRONIZATION_REPORT = {
  codeLow: 0x01,
  codeHigh: 0x04,
} as const

export type VisionApDonglePairingStatus = 'enter' | 'success' | 'overtime'

export interface VisionApDonglePairingEvent {
  status: VisionApDonglePairingStatus
}

/**
 * V0104 §2.2.9 / §2.2.11：版本 DATA 只有偏移 0/1 两字节。
 * LEN 取该 DATA 区长度，ADDR 取起始偏移；不套用 AD 的 LEN=6。
 */
export const VISION_AP_FIRMWARE_VERSION = {
  dataLength: 2,
  address: 0,
} as const

/**
 * V0106 §2.2.18：续航 DATA 共 4 字节，ADDR 从 0 起。
 * 高字节在前（大端），与 `0xA3` 基本信息区的小端字段相反。
 */
export const VISION_AP_MOUSE_BATTERY_RUNTIME = {
  dataLength: 4,
  address: 0,
} as const

export interface VisionApBatteryRuntime {
  min: number
  max: number
}

export interface VisionApFirmwareVersion {
  low: number
  high: number
  value: number
  text: string
}

export interface VisionApResponse {
  command: number
  length: number
  address: number
  ack: number
  data: Uint8Array
  rawData: Uint8Array
}

/**
 * 已抓包证实的功能变量区快照会话。
 *
 * DPI 与参数页共用完整 128 字节区域，不能以 DPI 命名，否则会掩盖参数页同样依赖该传输约束。
 * 所有写入都必须基于同一会话读取到的快照，原样保留未建模字段。
 */
export interface VisionApFunctionAreaSession {
  readFunctionAreaSnapshot(): Promise<Uint8Array>
  writeFunctionAreaSnapshot(area: Uint8Array): Promise<void>
}

/** 维盛 AP 鼠标按键区会话，仅暴露已确认的按键区读写原语。 */
export interface VisionApKeyAreaSession {
  readKeyArea(address: number, length: number): Promise<Uint8Array>
  writeKeyArea(address: number, data: Uint8Array): Promise<void>
}

/** 维盛 AP 鼠标宏区会话，宏区地址从总宏头开始连续计算。 */
export interface VisionApMacroAreaSession {
  readMacroArea(address: number, length: number): Promise<Uint8Array>
  writeMacroArea(address: number, data: Uint8Array): Promise<void>
}

/**
 * V0106 profile 名称区会话（BB/BC）。
 * 名称区是全局单区（不按 profile 分页），只暴露完整 128 字节的读改写原语。
 */
export interface VisionApProfileNameAreaSession {
  readProfileNameArea(): Promise<Uint8Array>
  writeProfileNameArea(area: Uint8Array): Promise<void>
}

/**
 * V0106 宏名称区会话（B9/BA）。
 * 与宏区 AA/AB 相互独立，只暴露完整 240 字节的读改写原语。
 */
export interface VisionApMacroNameAreaSession {
  readMacroNameArea(): Promise<Uint8Array>
  writeMacroNameArea(area: Uint8Array): Promise<void>
}

/**
 * 组装不包含 Report ID 的 63 字节 payload。
 * WebHID 与 NodeHID 都会由通道层补上 0x04，避免线上发送出 65 字节报文。
 * 校验和覆盖 CMD 至 64 字节报文末尾；功能区分包会保留读取到的尾部字节。
 */
export function createVisionApPayload(
  command: number,
  length = 0,
  address = 0,
  data?: Uint8Array,
  padding?: Uint8Array,
): Uint8Array {
  assertByte(command, 'CMD')
  assertDataLength(length)
  assertUint16(address, 'ADDR')

  if (data && data.byteLength > VISION_AP_MOUSE_HID.maximumDataLength) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `维盛 AP DATA 长度不能超过 ${VISION_AP_MOUSE_HID.maximumDataLength} 字节`,
    )
  }
  if (data && data.byteLength !== length) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '写功能区时 LEN 必须等于 DATA 长度')
  }
  const paddingLength = VISION_AP_MOUSE_HID.maximumDataLength - length
  if (padding && padding.byteLength !== paddingLength) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '维盛 AP 填充区长度与 LEN 不匹配')
  }

  const payload = new Uint8Array(VISION_AP_MOUSE_HID.payloadByteLength)
  payload[2] = command
  payload[3] = length
  payload[4] = address & 0xff
  payload[5] = (address >> 8) & 0xff
  if (data) payload.set(data, 7)
  if (padding) payload.set(padding, 7 + length)

  const checksum = calculatePacketChecksum(payload)
  payload[0] = checksum & 0xff
  payload[1] = (checksum >> 8) & 0xff
  return payload
}

/**
 * 配对进窗命令：与 Qt / 验证包一致，Cs 固定 `00 00`，不填维盛累加和。
 * 通道层仍补 Report ID `0x04`，线上即 `04 00 00 B1/92 …`。
 */
export function createVisionApPairingPayload(command: number): Uint8Array {
  assertByte(command, 'CMD')
  if (
    command !== VISION_AP_MOUSE_COMMAND.enterMousePairing &&
    command !== VISION_AP_MOUSE_COMMAND.enterDonglePairing
  ) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      'createVisionApPairingPayload 仅用于 0xB1/0x92 配对进窗',
    )
  }
  const payload = new Uint8Array(VISION_AP_MOUSE_HID.payloadByteLength)
  payload[2] = command
  return payload
}

/** 判断标准化 input payload 是否为指定命令的回显。 */
export function isVisionApCommandEcho(data: DataView, command: number): boolean {
  return data.byteLength >= 3 && data.getUint8(2) === command
}

/**
 * 抓包中的回包保留 Cs、CMD、LEN、ADDR，仅覆写 ACK 与 DATA。
 * 因此用请求头匹配回包，既允许非零 ACK 进入错误处理，也不会误收同命令的旧回包。
 */
export function isVisionApResponseForRequest(data: DataView, request: Uint8Array): boolean {
  if (data.byteLength < 6 || request.byteLength < 6) return false
  for (let index = 0; index < 6; index += 1) {
    if (data.getUint8(index) !== request[index]) return false
  }
  return true
}

/**
 * 解析通道已剥离 Report ID 的 input payload。
 * 抓包显示设备会保留请求校验和再覆写 DATA，响应不能据此重新验和。
 */
export function parseVisionApResponse(data: DataView, expectedCommand: number): VisionApResponse {
  if (data.byteLength < 7) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 响应长度不足 7 字节')
  }
  if (!isVisionApCommandEcho(data, expectedCommand)) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 响应 CMD 未回显当前请求')
  }

  const length = data.getUint8(3)
  if (length > VISION_AP_MOUSE_HID.maximumDataLength) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 响应 LEN 超出 56 字节')
  }
  if (data.byteLength < 7 + length) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 响应 DATA 长度不足')
  }

  const responseData = new Uint8Array(length)
  const rawData = new Uint8Array(data.byteLength - 7)
  for (let index = 0; index < length; index += 1) {
    responseData[index] = data.getUint8(7 + index)
  }
  for (let index = 0; index < rawData.byteLength; index += 1) {
    rawData[index] = data.getUint8(7 + index)
  }

  return {
    command: data.getUint8(2),
    length,
    address: data.getUint16(4, true),
    ack: data.getUint8(6),
    data: responseData,
    rawData,
  }
}

/**
 * 解析续航 DATA：`[0..1]` 最低小时、`[2..3]` 最高小时，均为高字节在前。
 * 示例 `00 32 00 64` → `{ min: 50, max: 100 }`。
 */
export function parseVisionApBatteryRuntime(data: Uint8Array): VisionApBatteryRuntime {
  if (data.byteLength < VISION_AP_MOUSE_BATTERY_RUNTIME.dataLength) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 续航 DATA 不足 4 字节')
  }
  return {
    min: (data[0]! << 8) | data[1]!,
    max: (data[2]! << 8) | data[3]!,
  }
}

/**
 * 解析版本 DATA：`[0]` 低 8 位，`[1]` 高 8 位，合成为小端 u16。
 * 展示为 `高字节.低字节` 两位小数，与云端 `version.json` 的 `FirmwareVersion`（如 `1.06`）对齐。
 */
export function parseVisionApFirmwareVersion(data: Uint8Array): VisionApFirmwareVersion {
  if (data.byteLength < VISION_AP_FIRMWARE_VERSION.dataLength) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, '维盛 AP 版本 DATA 不足 2 字节')
  }
  const low = data[0]!
  const high = data[1]!
  const value = low | (high << 8)
  return {
    low,
    high,
    value,
    text: `${high}.${String(low).padStart(2, '0')}`,
  }
}

/**
 * 解析 dongle 配对 consumer 上报。
 * - WebHID：reportId=`0x03`，data=`FF xx`
 * - Qt/整包：首字节或次字节为 `03 FF xx`
 */
export function parseVisionApDonglePairingReport(
  reportId: number,
  data: DataView,
): VisionApDonglePairingEvent | null {
  if (reportId === VISION_AP_MOUSE_HID.consumerReportId && data.byteLength >= 2) {
    if (data.getUint8(0) === VISION_AP_DONGLE_PAIRING_REPORT.codeLow) {
      return pairingStatusFromCodeHigh(data.getUint8(1))
    }
  }
  if (
    data.byteLength >= 3 &&
    data.getUint8(0) === VISION_AP_MOUSE_HID.consumerReportId &&
    data.getUint8(1) === VISION_AP_DONGLE_PAIRING_REPORT.codeLow
  ) {
    return pairingStatusFromCodeHigh(data.getUint8(2))
  }
  if (
    data.byteLength >= 4 &&
    data.getUint8(1) === VISION_AP_MOUSE_HID.consumerReportId &&
    data.getUint8(2) === VISION_AP_DONGLE_PAIRING_REPORT.codeLow
  ) {
    return pairingStatusFromCodeHigh(data.getUint8(3))
  }
  return null
}

/**
 * 识别 Function synchronization 主动上报。
 * 兼容通道已剥离 Report ID、整包以 03 开头，以及 Windows 抓包前置 00 三种形态。
 */
export function isVisionApFunctionSynchronizationReport(reportId: number, data: DataView): boolean {
  const { codeLow, codeHigh } = VISION_AP_FUNCTION_SYNCHRONIZATION_REPORT
  if (reportId === VISION_AP_MOUSE_HID.consumerReportId && data.byteLength >= 2) {
    return data.getUint8(0) === codeLow && data.getUint8(1) === codeHigh
  }
  if (data.byteLength >= 3) {
    if (
      data.getUint8(0) === VISION_AP_MOUSE_HID.consumerReportId &&
      data.getUint8(1) === codeLow &&
      data.getUint8(2) === codeHigh
    ) {
      return true
    }
  }
  return (
    data.byteLength >= 4 &&
    data.getUint8(1) === VISION_AP_MOUSE_HID.consumerReportId &&
    data.getUint8(2) === codeLow &&
    data.getUint8(3) === codeHigh
  )
}

function pairingStatusFromCodeHigh(codeHigh: number): VisionApDonglePairingEvent | null {
  if (codeHigh === VISION_AP_DONGLE_PAIRING_REPORT.enter) {
    return { status: 'enter' }
  }
  if (codeHigh === VISION_AP_DONGLE_PAIRING_REPORT.success) {
    return { status: 'success' }
  }
  if (codeHigh === VISION_AP_DONGLE_PAIRING_REPORT.overtime) {
    return { status: 'overtime' }
  }
  return null
}

function calculatePacketChecksum(payload: Uint8Array): number {
  let checksum = 0
  for (let index = 2; index < payload.byteLength; index += 1) {
    checksum = (checksum + payload[index]) & 0xffff
  }
  return checksum
}

function assertDataLength(length: number): void {
  if (!Number.isInteger(length) || length < 0 || length > VISION_AP_MOUSE_HID.maximumDataLength) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `维盛 AP LEN 必须为 0-${VISION_AP_MOUSE_HID.maximumDataLength} 的整数`,
    )
  }
}

function assertByte(value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xff) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field} 必须为 uint8`)
  }
}

function assertUint16(value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field} 必须为 uint16`)
  }
}
